/**
 * Frontend Error Reporter
 *
 * Captures three classes of browser-side errors and ships them to the
 * backend via POST /api/errors/report so they land in Admin → Error Logs
 * alongside server-side errors:
 *
 *   1. window 'error' — synchronous JS exceptions not caught by anything
 *   2. window 'unhandledrejection' — promise rejections nobody caught
 *   3. React render errors — via ErrorBoundary's componentDidCatch
 *
 * The reporter de-bounces 1 second per (errorName + first stack line) so a
 * render storm doesn't self-DDoS the report endpoint. Fire-and-forget at
 * the network layer; failures are silent (we are the error path).
 *
 * Phase 2 (see docs/audit-log-roadmap.md):
 *   - Capture breadcrumbs (last N user actions before the crash)
 *   - Decode minified production stacks via source maps
 *   - Forward to Sentry / DataDog in addition to our own backend
 */

import { errorLogsAPI } from '../services/api';

const DEBOUNCE_MS = 1000;
const recent = new Map(); // key -> timestamp

// Browser-extension URLs we never want to capture — these crashes come from
// the user's installed extensions (React DevTools, password managers, ad
// blockers, etc.) and have nothing to do with our app.
const EXTENSION_URL_RE = /(?:chrome|moz|safari|edge|opera)-extension:\/\//i;

function isExtensionError(payload) {
  if (payload?.url && EXTENSION_URL_RE.test(payload.url)) return true;
  if (payload?.stack && EXTENSION_URL_RE.test(payload.stack)) return true;
  return false;
}

function dedupeKey(payload) {
  const firstStackLine = (payload?.stack || '').split('\n').find((l) => l.trim()) || '';
  return `${payload?.name || 'Error'}|${firstStackLine.trim()}`;
}

function shouldSend(payload) {
  if (isExtensionError(payload)) return false;
  const key = dedupeKey(payload);
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEBOUNCE_MS) return false;
  recent.set(key, now);
  return true;
}

/**
 * Report a browser-side error. Safe to call from anywhere — never throws,
 * never blocks. Returns the API promise so callers (e.g. ErrorBoundary)
 * can opt to await it if needed.
 */
export function reportError(payload = {}) {
  try {
    if (!shouldSend(payload)) return Promise.resolve();
    const body = {
      name: payload.name || 'Error',
      message: payload.message || 'Unknown error',
      stack: payload.stack,
      url: payload.url || (typeof window !== 'undefined' ? window.location.href : undefined),
      lineno: payload.lineno,
      colno: payload.colno,
      componentStack: payload.componentStack,
      currentRoute: typeof window !== 'undefined' ? window.location.pathname : undefined,
    };
    return errorLogsAPI.reportClient(body);
  } catch (_) {
    // Never escalate — we are the error path itself.
    return Promise.resolve();
  }
}

let installed = false;

/**
 * Install global hooks. Idempotent — safe to call once on app mount.
 */
export function install() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    // `event.error` is the actual Error in modern browsers; fall back to
    // `event.message` if a third party blew up cross-origin (it can be null).
    const err = event.error;
    reportError({
      name: err?.name || 'Error',
      message: err?.message || event.message || 'window.onerror',
      stack: err?.stack,
      url: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const isErr = reason instanceof Error;
    reportError({
      name: 'UnhandledRejection',
      message: isErr ? reason.message : String(reason),
      stack: isErr ? reason.stack : undefined,
    });
  });
}

const errorReporter = { install, reportError };
export default errorReporter;
